import { useCallback, useState } from 'react';
import { useFig } from '../../session/FigProvider.js';
import type { Client } from '../../core/api/types.js';
import { EmptyState, ErrorState, RowSkeleton } from '../../components/States.js';
import { Dialog, Field } from '../../components/Dialog.js';
import { useResource } from './useResource.js';

/**
 * End-clients: create, rename, and cut off portal access for a whole client at once.
 *
 * `portalAccessDisabled` and a portal user's own `disabled` are two independent switches, on
 * purpose — restoring a client must not silently restore somebody who was turned off
 * individually. The copy has to make that distinction visible or an admin will assume one
 * undoes the other.
 */
export function Clients() {
  const { client: api } = useFig();
  const load = useCallback(() => api.listClients({ sortBy: 'Name', sortDirection: 'Asc' }), [api]);
  const { data: clients, error, busy, reload } = useResource<readonly Client[]>(load);
  const [creating, setCreating] = useState(false);

  if (error !== null) return <ErrorState error={error} onRetry={() => void reload()} />;

  // A Direct-mode agency has exactly one client — itself — and cannot create more (409).
  const directMode = clients?.length === 1 && clients[0]?.isSelf === true;

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">Clients</h1>
        {!directMode && (
          <button className="btn btn-primary btn-inline" onClick={() => setCreating(true)}>Add a client</button>
        )}
      </div>

      {directMode && (
        <p className="banner" role="status">
          This account manages its own calls rather than clients’, so there’s just the one.
        </p>
      )}

      {clients === null && busy && <RowSkeleton count={3} />}

      {clients?.length === 0 && (
        <EmptyState
          title="No clients yet"
          body="A client is one of the businesses you run an assistant for. Add one, then connect their ElevenLabs account."
        />
      )}

      {clients && clients.length > 0 && (
        <table className="table">
          <caption className="sr-only">Clients, {clients.length}</caption>
          <thead>
            <tr><th scope="col">Name</th><th scope="col">Portal access</th>
              <th scope="col"><span className="sr-only">Actions</span></th></tr>
          </thead>
          <tbody>
            {clients.map((c) => <ClientRow key={c.id} row={c} onChanged={() => void reload()} />)}
          </tbody>
        </table>
      )}

      <CreateDialog open={creating} onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); void reload(); }} />
    </>
  );
}

function ClientRow({ row, onChanged }: { row: Client; onChanged: () => void }) {
  const { client: api } = useFig();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function toggle() {
    setBusy(true); setError(null);
    try {
      await api.updateClient(row.id, { portalAccessDisabled: !row.portalAccessDisabled });
      onChanged();
    } catch (e) { setError(e instanceof Error ? e.message : 'That did not work.'); }
    finally { setBusy(false); }
  }

  return (
    <tr>
      <th scope="row" className="cell-name">
        {row.name}{row.isSelf && <span className="pill pill-invited">This agency</span>}
      </th>
      <td>{row.portalAccessDisabled ? 'Closed' : 'Open'}</td>
      <td className="cell-actions">
        <button className="btn btn-small" disabled={busy} onClick={() => void toggle()}>
          {row.portalAccessDisabled ? 'Re-open portal' : 'Close portal'}
        </button>
        {error && <span className="hint hint-error" role="alert">{error}</span>}
      </td>
    </tr>
  );
}

function CreateDialog({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: () => void;
}) {
  const { client: api } = useFig();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api.createClient(name.trim()); setName(''); onCreated(); }
    catch (err) { setError(err instanceof Error ? err.message : 'That did not work.'); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Add a client">
      <form onSubmit={submit} noValidate>
        <Field id="client-name" label="Business name" value={name} autoComplete="organization"
          onChange={(e) => setName(e.target.value)} required
          hint="What their customers would call them. You can change it later." />
        {error && <p className="hint hint-error" role="alert">{error}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-inline" disabled={busy || !name.trim()}>
            {busy ? 'Adding…' : 'Add client'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
